// Step 2's resume, for an actor who already has some (decided 2026-10-29, option A): one row
// shows the resume this submission sends, with Change; the sheet lists theirs to pick from,
// with "Upload a new PDF". Choosing one uploads nothing and changes nothing in the library.
//
// A PDF and a resume typed on theWorkbook (PM's Pdf, Simple, Editor) are told apart by the icon
// alone, never a word. Both icons are cwb-icons' (cwbfw/src/widgets/common/cwb-icons):
// icon-teamshare-resume for a PDF, icon-resumes for a typed one.

import React from "react";
import styled, { css } from "styled-components";
import i18next from "i18next";
import { IMediaAssetDto } from "../../../shared/api/submission/types";
import { MEMBERSHIP_BLUE } from "./membershipButton";

const isTyped = (resume: IMediaAssetDto) => resume.resumeType === "typed";

export const ResumeIcon: React.FC<{ resume: IMediaAssetDto; size?: number }> = ({ resume, size = 30 }) =>
  isTyped(resume) ? (
    <svg width={size} height={size} viewBox="0 0 32 32" role="img" aria-label={i18next.t("Typed resume")} focusable="false">
      <path
        fill="#0b7cc2"
        d="M14 12h12v2h-12zM14 16h12v2h-12zM14 20h12v2h-12zM14 24h8v2h-8zM30 6h-6v-4c0-1.1-0.9-2-2-2h-20c-1.1 0-2 0.9-2 2v22c0 1.1 0.9 2 2 2h6v4c0 1.1 0.9 2 2 2h20c1.1 0 2-0.9 2-2v-22c0-1.1-0.9-2-2-2zM8 8v15h-5v-20h18v3h-11c-1.1 0-2 0.9-2 2zM29 29h-18v-20h18v20z"
      />
    </svg>
  ) : (
    <svg width={size * 0.8} height={size} viewBox="0 0 26 32" role="img" aria-label={i18next.t("PDF resume")} focusable="false">
      <path
        fill="#c3303a"
        d="M3.157 0h10.799c1.172 0 2.296 0.466 3.125 1.295l6.883 6.883c0.829 0.829 1.295 1.953 1.295 3.125v17.113c0 1.737-1.421 3.157-3.157 3.157h-18.944c-1.737 0-3.157-1.421-3.157-3.157v-25.259c0-1.737 1.421-3.157 3.157-3.157zM14.205 9.798c0 0.488 0.396 0.884 0.884 0.884h7.114c0.234 0 0.459-0.093 0.625-0.259 0.345-0.345 0.345-0.905 0-1.25l-7.114-7.114c-0.166-0.166-0.391-0.259-0.625-0.259-0.488 0-0.884 0.396-0.884 0.884v7.114z"
      />
    </svg>
  );

// cwb-icons' icon-upload, for "Upload a new PDF".
export const UploadIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M10 12h-4l10-9.999 10 9.999h-4v12h-12v-12zM24 22v4h-16v-4h-4.001v7.999h24.001v-7.999h-4z" />
  </svg>
);

// "2 pages · Updated Oct 12, 2026": the pages a PDF has, when PM knows them, and the last change,
// in the page's language.
export const resumeDetails = (resume: IMediaAssetDto, language: string = i18next.language || "en"): string => {
  const parts: string[] = [];
  if (!isTyped(resume) && resume.pageCount) {
    parts.push(
      resume.pageCount === 1 ? i18next.t("1 page") : i18next.t("{{count}} pages", { count: resume.pageCount })
    );
  }
  const updated = Date.parse(resume.uploadedAtUtc || "");
  if (updated) {
    let date: string;
    try {
      date = new Date(updated).toLocaleDateString(language, { year: "numeric", month: "short", day: "numeric" });
    } catch (e) {
      date = new Date(updated).toLocaleDateString();
    }
    parts.push(i18next.t("Updated {{date}}", { date }));
  }
  return parts.join(" · ");
};

// The resume this submission sends, on the page.
export const ResumeSummary: React.FC<{
  resume: IMediaAssetDto;
  onChange: () => void;
  highlight?: boolean;
}> = ({ resume, onChange, highlight }) => {
  const details = resumeDetails(resume);
  return (
    <Summary highlight={highlight}>
      <IconSlot>
        <ResumeIcon resume={resume} size={32} />
      </IconSlot>
      <Text>
        <Name>{resume.fileName}</Name>
        {details && <Details>{details}</Details>}
      </Text>
      <ChangeButton type="button" onClick={onChange}>
        {i18next.t("Change")}
      </ChangeButton>
    </Summary>
  );
};

// The sheet's list: one choice each, the one picked marked.
export const ResumeChoices: React.FC<{
  resumes: IMediaAssetDto[];
  selectedId?: number | null;
  onSelect: (resume: IMediaAssetDto) => void;
}> = ({ resumes, selectedId, onSelect }) => (
  <Choices role="group" aria-label={i18next.t("Your resumes")}>
    {resumes.map((r) => {
      const selected = r.assetId === selectedId;
      const details = resumeDetails(r);
      return (
        <Choice key={r.assetId} type="button" selected={selected} aria-pressed={selected} onClick={() => onSelect(r)}>
          <IconSlot>
            <ResumeIcon resume={r} size={32} />
          </IconSlot>
          <Text>
            <ChoiceName>{r.fileName}</ChoiceName>
            {details && <Details>{details}</Details>}
          </Text>
          <Radio selected={selected} aria-hidden="true">
            {selected && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2.5 6.2l2.3 2.3 4.7-5" />
              </svg>
            )}
          </Radio>
        </Choice>
      );
    })}
  </Choices>
);

// A blue ring that fades once: what step 2 picked by itself, so it doesn't just appear.
export const pickedGlow = css`
  @keyframes sf-picked-glow {
    0%,
    70% {
      box-shadow: 0 0 0 3px rgba(15, 155, 240, 0.55);
    }
    100% {
      box-shadow: 0 0 0 3px rgba(15, 155, 240, 0);
    }
  }
  animation: sf-picked-glow 2.2s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Summary: any = styled.div`
  display: flex;
  align-items: center;
  background-color: #f7f9fb;
  border-radius: 6px;
  padding: 10px 6px 10px 14px;
  margin-bottom: 10px;
  min-height: 48px;
  ${(p: any) => p.highlight && pickedGlow}
`;

const IconSlot = styled.span`
  flex: none;
  width: 32px;
  display: flex;
  justify-content: center;
  margin-right: 12px;
`;

const Text = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1d2b44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChoiceName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1d2b44;
`;

const Details = styled.span`
  font-size: 12px;
  color: #53627c;
  margin-top: 2px;
`;

const ChangeButton = styled.button`
  flex: none;
  min-height: 44px;
  padding: 0 10px;
  border: none;
  background: none;
  color: #0b7cc2;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border-radius: 6px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px #0f9bf0;
  }
`;

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  > * + * {
    margin-top: 8px;
  }
`;

const Choice: any = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px;
  border: 1px solid #c9d3e0;
  border-radius: 8px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;

  ${(p: any) =>
    p.selected &&
    css`
      border-color: ${MEMBERSHIP_BLUE.normal};
      box-shadow: 0 0 0 1px ${MEMBERSHIP_BLUE.normal};
      background: #f2f9fe;
    `}

  &:focus-visible {
    outline: 2px solid #0f9bf0;
    outline-offset: 2px;
  }
`;

const Radio: any = styled.span`
  flex: none;
  width: 22px;
  height: 22px;
  margin-left: 10px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 2px solid #c9d3e0;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p: any) =>
    p.selected &&
    css`
      border: none;
      background: ${MEMBERSHIP_BLUE.normal};
    `}
`;
